import { achievementNftConfigured } from "./chain-config.js";
import { getChainApp } from "./chain-app.js";
import {
  getClaimProof,
  loadBackendConfig,
  PM_ONBOARD_VERIFIED,
} from "./gateway-api.js";
import { loadTradingKey } from "./trading-key-store.js";
import {
  buildTraderAuthArg,
  hexToBytes,
  signAchievementClaim,
} from "./trader-crypto.js";
import { showGlobalTx, trackTx } from "./tx-status.js";

const CUTOVER_MSG =
  "Achievement NFT claim is not live until Phase G cutover - achievementNft must be pinned in contracts.testnet.json.";

function parseRootBytes(proof) {
  const raw = proof.root_bytes ?? proof.root;
  if (!raw) throw new Error("Claim proof missing root");
  const bytes = hexToBytes(String(raw).replace(/^0x/i, ""));
  if (bytes.length !== 32) {
    throw new Error("Claim proof root must be 32 bytes");
  }
  return bytes;
}

function parseProofSiblings(proof) {
  if (!Array.isArray(proof.proof)) {
    throw new Error("Claim proof missing Merkle siblings");
  }
  return proof.proof.map((hex) =>
    Array.from(hexToBytes(String(hex).replace(/^0x/i, "")))
  );
}

/**
 * Fetch a streak claim proof, sign with the trading key, and submit
 * `achievement-nft.claim` on testnet.
 *
 * @param {{ commitmentHex: string, epoch: number, achievementType?: number, log?: Function }} opts
 * @returns {Promise<{ txHash: string }>}
 */
export async function submitAchievementClaim({
  commitmentHex,
  epoch,
  achievementType = 1,
  log,
}) {
  if (!achievementNftConfigured()) {
    throw new Error(CUTOVER_MSG);
  }

  const key = loadTradingKey();
  if (!key?.sk_hex) {
    throw new Error("Connect a trading key in Account settings");
  }

  const cfg = loadBackendConfig();
  const proof = await getClaimProof(cfg, commitmentHex, {
    epoch,
    type: achievementType,
  });

  const rootBytes = parseRootBytes(proof);
  const proofSiblings = parseProofSiblings(proof);
  const value = Number(proof.value);
  const claimEpoch = Number(proof.epoch ?? epoch);
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error("Claim proof missing streak value");
  }
  if (!Number.isFinite(claimEpoch) || claimEpoch <= 0) {
    throw new Error("Claim proof missing epoch");
  }

  const { signatureHex } = await signAchievementClaim({
    skHex: key.sk_hex,
    achievementType,
    value,
    epoch: claimEpoch,
    rootBytes,
  });
  const auth = buildTraderAuthArg({
    pkBase58: key.pk_base58,
    nonce: 0,
    signatureHex,
  });

  const onboard = achievementType === PM_ONBOARD_VERIFIED;
  const ui = showGlobalTx({
    label: onboard ? "Claim onboard achievement" : "Claim achievement NFT",
  });
  const dusk = await getChainApp();
  const tx = await dusk.writeContract({
    contract: "achievementNft",
    functionName: "claim",
    args: {
      auth,
      achievement_type: achievementType,
      value,
      epoch: claimEpoch,
      proof: proofSiblings,
    },
    privacy: "public",
    display: {
      summary: onboard
        ? "Claim onboard achievement"
        : `Claim streak achievement (type ${achievementType}, value ${value})`,
      achievementType,
      value,
      epoch: claimEpoch,
      note: onboard
        ? "Lab/test NFT - does not unlock Create."
        : "NFT mints to your connected Moonlight wallet.",
    },
  });
  const tracked = await trackTx(tx, { ui, autoDismissMs: 10_000 });
  log?.(`achievement claim tx ${tracked.hash}`);
  return { txHash: tracked.hash };
}
